import React, { useEffect } from 'react'
import { Form, Radio, Typography } from 'antd'
import { QuestionnaireItem } from '@medplum/fhirtypes'
import { useAssessmentContext } from 'contexts/AssessmentContext'
import { useQAAssessmentActionsContext } from 'contexts/QAAssessmentActionsContext'
import { getCssSelectorForLinkId } from 'utils/helpers'
import { QuestionnaireAnswerType } from 'components/Assessments/assessmentType'
import { useQAAssessmentCommentsContext } from 'contexts/QAAssessmentCommentsContext'
import { getCommentQuestionLinkId } from 'components/QA/Assessments/helpers'
import { HalfWidthContainer, ItemWithTextDetailsContainer } from './styles'
import { extractDataFromReference } from './helpers'
import { QuestionItemTextArea } from './QuestionItemTextArea'
import { useValidateAndUpdateValues } from '../PatAssessment/hooks/useValidateAndUpdateFormValues'
import { getNestedItems } from '../helpers'

export const QuestionColumnRadioSelect = ({
  question: field,
  data,
  dataReference
}): JSX.Element => {
  const {
    answers,
    assessmentStatusState: { inQA, inQAEdit }
  } = useAssessmentContext()
  const validateAndUpdateFormValues = useValidateAndUpdateValues()
  const { createCommentClickHandler, disableHover } = useQAAssessmentActionsContext()

  const { sortedComments: comments, focusedCommentId } = useQAAssessmentCommentsContext()
  const focusedComment = comments.find((c) => c.id === focusedCommentId)
  const focusedCommentLinkId = focusedComment ? getCommentQuestionLinkId(focusedComment) : null

  let question = field
  let notes
  if (field.item) {
    const { question: nestedQuestion, notes: nestedNotes } = getNestedItems(
      field.item as QuestionnaireItem[]
    )
    question = nestedQuestion
    notes = nestedNotes
  }
  const isItemDataExternal = data && dataReference
  const defaultValue = extractDataFromReference(data, dataReference)
  const defaultAnswer = question?.answerOption?.find(
    (option) => option?.valueCoding?.code === defaultValue
  )

  let initialSelectedOption
  if (isItemDataExternal) {
    initialSelectedOption = defaultValue
  } else if (question.linkId) {
    const savedAnswer = answers[question.linkId]
    initialSelectedOption = savedAnswer?.valueCoding?.display
  }

  const options = question?.answerOption?.map((option) => ({
    label: option?.valueCoding?.display,
    value: option?.valueCoding?.code
  }))

  const handleRadioChange = (e): void => {
    const selectedValue = e.target.value
    if (question.type && question.linkId && question.text) {
      validateAndUpdateFormValues({
        type: question.type as QuestionnaireAnswerType,
        linkId: question.linkId,
        text: question.text,
        value: { code: selectedValue, display: selectedValue }
      })
    }
  }

  const renderNotesInput = (): JSX.Element | null => {
    if (notes) {
      return (
        <div style={{ paddingTop: '1rem' }}>
          <QuestionItemTextArea question={notes} />
        </div>
      )
    }
    return null
  }
  useEffect(() => {
    if (isItemDataExternal) {
      validateAndUpdateFormValues({
        type: question.type as QuestionnaireAnswerType,
        linkId: question.linkId,
        text: question.text,
        value: defaultAnswer?.valueCoding
      })
    }
  }, [
    question.answerOption,
    question.linkId,
    question.text,
    question.type,
    isItemDataExternal,
    defaultAnswer
  ])

  return (
    <ItemWithTextDetailsContainer
      id={getCssSelectorForLinkId(question.linkId)}
      inQA={inQA}
      inQAEdit={inQAEdit}
      disableHover={disableHover}
      isFocused={question.linkId === focusedCommentLinkId}
      onClick={() => question.linkId && createCommentClickHandler(question.linkId)}
    >
      <HalfWidthContainer>
        <Typography.Title level={5}>{question.text}</Typography.Title>
      </HalfWidthContainer>
      <HalfWidthContainer>
        <Form.Item
          name={question.linkId}
          initialValue={initialSelectedOption}
          rules={[{ required: question.required, message: 'Please select an option' }]}
        >
          <Radio.Group
            name={question.linkId}
            disabled={question.readOnly || undefined}
            onChange={handleRadioChange}
            style={{ marginBottom: notes && '1rem' }}
          >
            {options?.map((option, index) => (
              <Radio key={index} value={option.value}>
                {option.label}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
        <div>{notes && renderNotesInput()}</div>
      </HalfWidthContainer>
    </ItemWithTextDetailsContainer>
  )
}
